import { useEffect, useRef, useCallback } from "react";
import { pb } from "@/lib/pocketbase";

interface UseActivityDetectionOptions {
  userId: string;
  userRole: string;
  isEnabled: boolean;
  inactivityTimeout?: number; // in milliseconds, default 5 minutes
  onInactive?: () => void;
}

export const useActivityDetection = ({
  userId,
  userRole,
  isEnabled,
  inactivityTimeout = 30 * 60 * 1000, // 30 minutes (shorter than logout timeout)
  onInactive,
}: UseActivityDetectionOptions) => {
  const lastActivityRef = useRef<number>(Date.now());
  const lastDbUpdateRef = useRef<number>(0);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(true);

  const updateActivity = useCallback(async () => {
    if (!userId || userRole !== "tutor" || !isEnabled) return;

    const now = Date.now();
    lastActivityRef.current = now;

    // Only update DB if at least 30 seconds have passed since last update
    if (now - lastDbUpdateRef.current < 30000) {
      return;
    }

    lastDbUpdateRef.current = now;

    // Store last activity time in localStorage for cross-component access
    localStorage.setItem("lastActiveTime", now.toString());

    // Reset to active if was inactive
    if (!isActiveRef.current) {
      isActiveRef.current = true;
      console.log("User became active again");
    }

    try {
      const tutorProfile = await pb.collection("tutors").getFirstListItem(`user = "${userId}"`);
      const statusRecord = await pb
        .collection("tutor_online_status")
        .getFirstListItem(`tutorId = "${tutorProfile.id}"`);

      await pb.collection("tutor_online_status").update(statusRecord.id, {
        lastActivity: new Date().toISOString(),
        isOnline: true,
      });
    } catch (error) {
      console.error("Activity update error:", error);
    }

    // Reset inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(async () => {
      if (!isActiveRef.current) return; // Already marked inactive

      isActiveRef.current = false;
      console.log("User marked as inactive");
      onInactive?.();

      // Mark as offline due to inactivity
      try {
        const tutorProfile = await pb.collection("tutors").getFirstListItem(`user = "${userId}"`);
        const statusRecord = await pb
          .collection("tutor_online_status")
          .getFirstListItem(`tutorId = "${tutorProfile.id}"`);

        await pb.collection("tutor_online_status").update(statusRecord.id, {
          isOnline: false,
          lastSeen: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Inactivity marking error:", error);
      }
    }, inactivityTimeout);
  }, [userId, userRole, isEnabled, inactivityTimeout, onInactive]);

  useEffect(() => {
    if (!isEnabled || !userId || userRole !== "tutor") {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      return;
    }

    // Throttle function to avoid too many updates
    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledUpdate = () => {
      if (throttleTimeout) return;

      throttleTimeout = setTimeout(() => {
        updateActivity();
        throttleTimeout = null;
      }, 10000); // Throttle to once per 10 seconds
    };

    // Activity event listeners - reduced to only meaningful interactions
    const events = ["mousedown", "keypress", "click"];

    events.forEach((event) => {
      window.addEventListener(event, throttledUpdate);
    });

    // Initial activity update
    updateActivity();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, throttledUpdate);
      });

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [userId, userRole, isEnabled, updateActivity]);

  return { updateActivity };
};
